<template>
  <!-- See ./README.md for permission API and component usage examples. -->
  <v-tooltip
    v-model="isOpen"
    :disabled="!disabled"
    :open-delay="250"
    :class="['disabled-action-tooltip', { 'full-width': fullWidth }]"
    top
  >
    <template #activator="{ on }">
      <span
        :class="['disabled-action-activator', { 'full-width': fullWidth }]"
        :tabindex="disabled ? 0 : null"
        :role="disabled ? 'group' : null"
        :aria-disabled="disabled ? 'true' : null"
        :aria-label="disabled ? `${label} unavailable` : null"
        :aria-describedby="disabled ? tooltipId : null"
        v-on="on"
        @click="onActivatorClick"
        @focusin="onActivatorFocus"
        @blur="isOpen = false"
        @keydown.esc="onEscapeKeydown"
        @keyup.esc="onEscapeKeyup"
      >
        <slot :disabled="disabled" />
        <span v-if="disabled" :id="tooltipId" class="disabled-action-description">
          {{ reasons.join(' ') }}
        </span>
      </span>
    </template>
    <div role="tooltip">
      <strong>{{ label }} unavailable</strong>
      <div>One or more of these requirements are not met:</div>
      <ul class="mb-0 pl-4">
        <li v-for="reason in reasons" :key="reason">{{ reason }}</li>
      </ul>
    </div>
  </v-tooltip>
</template>

<script lang="ts">
import { computed, ref, watch } from 'vue'
import type { PermissionResult } from './permission'

let nextTooltipId = 0
</script>

<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    permission: PermissionResult
    label: string
    fullWidth?: boolean
  }>(),
  { fullWidth: false },
)

const tooltipId = `disabled-action-tooltip-${++nextTooltipId}`
const disabled = computed(() => !props.permission.allowed)
const reasons = computed(() => props.permission.reasons)
const isOpen = ref(false)
let handledEscape = false

watch(disabled, (value) => {
  if (!value) isOpen.value = false
})

const onActivatorClick = (event: MouseEvent) => {
  if (!disabled.value) return
  event.stopPropagation()
  isOpen.value = true
}

const onActivatorFocus = () => {
  if (disabled.value) isOpen.value = true
}

const onEscapeKeydown = (event: KeyboardEvent) => {
  if (!event.repeat) handledEscape = isOpen.value
  if (!handledEscape) return
  event.stopPropagation()
  isOpen.value = false
}

const onEscapeKeyup = (event: KeyboardEvent) => {
  // FormLayout dismisses on keyup. Consume the key that closed this tooltip,
  // while allowing a subsequent Escape to dismiss the sheet.
  if (handledEscape) event.stopPropagation()
  handledEscape = false
}
</script>

<style scoped>
.disabled-action-tooltip {
  display: inline-block;
}

.disabled-action-tooltip.full-width {
  display: block;
  width: 100%;
}

.disabled-action-activator {
  display: inline-block;
  vertical-align: middle;
}

.disabled-action-activator.full-width {
  display: block;
  width: 100%;
}

.disabled-action-description {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
